import {
  Component,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { InviteService } from "./invite.service";
import { ToastrService } from "ngx-toastr";
import {
  BulkUploadComponent,
  excelDataAndDataTypeInterface,
} from "src/app/Shared Utilities/bulk-upload/bulk-upload.component";
import { UserLoginService } from "src/app/login/userlogin.service";

/*
 * Adjust these three paths to match your project structure.
 */
import { BuildingService } from
  "../../Interface & Service/building/building.service";

import { FlatService } from
  "../../Interface & Service/flat/flat.service";

import { UserAffiliationMappingService } from
  "../../Interface & Service/userAffiliationMapping/userAffiliationMapping.service";

@Component({
  selector: "app-invite",
  templateUrl: "./invite.component.html",
  styleUrls: ["./invite.component.css"],
})
export class InviteComponent implements OnInit {
  inviteForm!: FormGroup;
  showForm: boolean = false;

  selectedStatus: string = "Initiated";

  inviteList: any[] = [];

  /* PAGINATION */
  currentPage: number = 1;
  pageSize: number = 10;

  loading: boolean = false;

  /* Society context */
  selectedSocietyId: number = 0;

  /* Building dropdown (identifier1) */
  buildingList: any[] = [];

  /* Flat dropdown (identifier2) */
  flatList: any[] = [];

  /* ACTION MENU / EDIT STATE */
  activeMenuIndex: number | null = null;
  editingInvite: any | null = null;

  @ViewChild("mobileNoInput")
  mobileNoInput?: ElementRef<HTMLInputElement>;

  private userLoginService = inject(UserLoginService);
  private affiliationMappingService = inject(UserAffiliationMappingService);
  private buildingService = inject(BuildingService);
  private flatService = inject(FlatService);
  private User: any = this.userLoginService.User();

  constructor(
    private fb: FormBuilder,
    private service: InviteService,
    private toastr: ToastrService,
    private bulkUploadService: BulkUploadComponent,
  ) {}

  ngOnInit(): void {
    this.inviteForm = this.fb.group({
      primaryName: [""],   // Flat Owner Name
      secondaryName: [""], // Flat Member Name
      mobileNo: ["", [Validators.required, Validators.pattern("^[0-9]{10}$")]],
      identifier1: [""],   // BuildingId
      identifier2: [""],   // FlatId
    });

    /*
     * When building changes, reload flat list and clear selected flat.
     * Skips during edit-patch (see editInvite).
     */
    this.inviteForm
      .get("identifier1")
      ?.valueChanges.subscribe((buildingId) => {
        if (this.editingInvite && !this.buildingList.length) {
          return;
        }
        this.onBuildingChange(buildingId);
      });

    /* 1) Resolve society → 2) load buildings → 3) load invite list */
    this.initializeSocietyContext();
  }

  /* ============================================================
   *  SOCIETY CONTEXT
   * ============================================================ */
  private initializeSocietyContext(): void {
    const existingSocietyId = this.getSocietyId();

    if (existingSocietyId > 0) {
      this.selectedSocietyId = existingSocietyId;
      localStorage.setItem("societyId", String(existingSocietyId));
      this.loadBuildings();
      this.readInviteList();
      return;
    }

    const userId = Number(
      this.User?.userId ||
        this.User?.UserId ||
        localStorage.getItem("userId") ||
        localStorage.getItem("UserId") ||
        0,
    );

    if (!userId) {
      this.inviteList = [];
      this.toastr.error("Society Id not found for logged in user.");
      return;
    }

    this.affiliationMappingService
      .SmUserAffiliationMapping_ReadByUserId({ userId })
      .subscribe({
        next: (res: any) => {
          const mappings = res?.items || res?.Items || [];

          const activeSocietyMapping =
            mappings.find(
              (item: any) =>
                Number(
                  item?.societyId ??
                    item?.SocietyId ??
                    item?.societyID ??
                    0,
                ) > 0 &&
                Number(item?.isActive ?? item?.IsActive ?? 1) === 1,
            ) ||
            mappings.find(
              (item: any) =>
                Number(
                  item?.societyId ??
                    item?.SocietyId ??
                    item?.societyID ??
                    0,
                ) > 0,
            );

          this.selectedSocietyId = Number(
            activeSocietyMapping?.societyId ??
              activeSocietyMapping?.SocietyId ??
              activeSocietyMapping?.societyID ??
              0,
          );

          if (!this.selectedSocietyId) {
            this.inviteList = [];
            this.toastr.error("Society Id not found for logged in user.");
            return;
          }

          localStorage.setItem(
            "societyId",
            String(this.selectedSocietyId),
          );

          console.log("Resolved Society Id", this.selectedSocietyId);

          this.loadBuildings();
          this.readInviteList();
        },

        error: (err: any) => {
          console.log("Society Mapping Error", err);
          this.inviteList = [];
          this.toastr.error("Unable to resolve Society Id.");
        },
      });
  }

  /* ============================================================
   *  BUILDING (identifier1)
   * ============================================================ */
  private loadBuildings(): void {
    const payload = { societyId: this.selectedSocietyId };

    console.log("Building Read Payload", payload);

    this.buildingService
      .Building_ReadBySocietyId(payload)
      .subscribe({
        next: (res: any) => {
          console.log("Building Response", res);

          const rows =
            res?.items ||
            res?.Items ||
            res?.data ||
            res?.Data ||
            [];

          this.buildingList = Array.isArray(rows) ? rows : [];
        },

        error: (err: any) => {
          console.log("Building Error", err);
          this.buildingList = [];
        },
      });
  }

  /* ============================================================
   *  FLAT (identifier2) — depends on selected building
   * ============================================================ */
  onBuildingChange(buildingId: any): void {
    /*
     * Clear selected flat whenever the building changes.
     */
    this.inviteForm.patchValue({ identifier2: "" }, { emitEvent: false });
    this.flatList = [];

    const id = Number(buildingId || 0);

    if (!id) {
      return;
    }

    const payload = { buildingId: id };

    console.log("Flat Read Payload", payload);

    /*
     * IMPORTANT: Flat_ReadByBuildingId lives on FlatService,
     * NOT on BuildingService.
     */
    this.flatService
      .Flat_ReadByBuildingId(payload)
      .subscribe({
        next: (res: any) => {
          console.log("Flat Response", res);

          const rows =
            res?.items ||
            res?.Items ||
            res?.data ||
            res?.Data ||
            [];

          this.flatList = Array.isArray(rows) ? rows : [];
        },

        error: (err: any) => {
          console.log("Flat Error", err);
          this.flatList = [];
        },
      });
  }

  /* ============================================================
   *  ADD / EDIT / CLOSE
   * ============================================================ */
  openAddInvite() {
    this.editingInvite = null;
    this.activeMenuIndex = null;
    this.flatList = [];
    this.inviteForm.reset();
    this.showForm = true;
  }

  closeForm() {
    this.showForm = false;
    this.editingInvite = null;
    this.activeMenuIndex = null;
    this.flatList = [];

    this.inviteForm.reset();
  }

  /* ============================================================
   *  ACTION MENU
   * ============================================================ */
  toggleActionMenu(index: number, event: MouseEvent): void {
    event.stopPropagation();

    this.activeMenuIndex =
      this.activeMenuIndex === index ? null : index;
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (!target.closest(".invite-action-wrapper")) {
      this.activeMenuIndex = null;
    }
  }

  editInvite(item: any, event?: MouseEvent): void {
    event?.stopPropagation();

    this.activeMenuIndex = null;
    this.editingInvite = item;

    const buildingId = String(item?.identifier1 ?? "");
    const flatId = String(item?.identifier2 ?? "");
    const numericBuildingId = Number(buildingId || 0);

    const patchForm = () => {
      this.inviteForm.patchValue(
        {
          primaryName: item?.primaryName || "",
          secondaryName: item?.secondaryName || "",
          mobileNo: item?.mobileNo || "",
          identifier1: buildingId,
          identifier2: flatId,
        },
        { emitEvent: false },
      );

      this.showForm = true;
    };

    if (numericBuildingId > 0) {
      /*
       * FlatService again — NOT BuildingService.
       */
      this.flatService
        .Flat_ReadByBuildingId({ buildingId: numericBuildingId })
        .subscribe({
          next: (res: any) => {
            const rows =
              res?.items ||
              res?.Items ||
              res?.data ||
              res?.Data ||
              [];

            this.flatList = Array.isArray(rows) ? rows : [];
            patchForm();
          },
          error: (err: any) => {
            console.log("Flat Error", err);
            this.flatList = [];
            patchForm();
          },
        });
    } else {
      patchForm();
    }
  }

  deleteInvite(item: any, event?: MouseEvent): void {
    event?.stopPropagation();

    this.activeMenuIndex = null;

    const inviteId = Number(item?.inviteId || 0);

    if (!inviteId) {
      this.toastr.error("Invite Id not found. Cannot delete this record.");
      return;
    }

    const displayName =
      String(item?.primaryName || "").trim() ||
      String(item?.mobileNo || "").trim() ||
      "this invite";

    const confirmed = window.confirm(
      `Are you sure you want to delete ${displayName}?`,
    );

    if (!confirmed) {
      return;
    }

    const payload = {
      inviteId,
      actionUser: this.getActionUser(),
    };

    console.log("Delete Invite Payload", payload);

    this.service.PreEnrollmentInvite_Delete(payload).subscribe({
      next: () => {
        this.toastr.success("Invite deleted successfully");
        this.readInviteList();
      },

      error: (err: any) => {
        console.log("Delete Invite Error", err);
        this.toastr.error(
          err?.error?.message ||
            err?.message ||
            "Unable to delete invite",
        );
      },
    });
  }

  /* ============================================================
   *  READ LIST
   * ============================================================ */
  readInviteList() {
    const societyId = this.getSocietyId();

    if (!societyId) {
      console.log("Read Invite skipped because Society Id is 0");
      this.inviteList = [];
      return;
    }

    const payload = {
      moduleType: "Society",
      moduleId: societyId,
    };

    console.log("Read Invite Payload", payload);

    this.service.PreEnrollmentInvite_ReadByModule(payload).subscribe({
      next: (res: any) => {
        console.log("Read Invite Response", res);

        const rows = this.extractInviteRows(res);

        this.inviteList = rows.map((item: any) =>
          this.normalizeInviteRow(item),
        );

        if (this.currentPage > this.totalPages) {
          this.currentPage = this.totalPages;
        }

        console.log("Invite Grid List", this.inviteList);
      },

      error: (err) => {
        console.log("Read Invite Error", err);
        this.inviteList = [];
      },
    });
  }

  private extractInviteRows(res: any): any[] {
    const rows =
      res?.items ??
      res?.Items ??
      res?.data?.items ??
      res?.data?.Items ??
      res?.Data?.items ??
      res?.Data?.Items ??
      res?.data ??
      res?.Data ??
      [];

    return Array.isArray(rows) ? rows : [];
  }

  private normalizeInviteRow(item: any): any {
    return {
      ...item,

      inviteId: Number(item?.inviteId ?? item?.InviteId ?? 0),

      moduleType: item?.moduleType ?? item?.ModuleType ?? "Society",

      moduleId: Number(item?.moduleId ?? item?.ModuleId ?? 0),

      primaryName: item?.primaryName ?? item?.PrimaryName ?? "",

      secondaryName: item?.secondaryName ?? item?.SecondaryName ?? "",

      mobileNo: String(item?.mobileNo ?? item?.MobileNo ?? ""),

      identifier1: item?.identifier1 ?? item?.Identifier1 ?? "",

      identifier2: item?.identifier2 ?? item?.Identifier2 ?? "",

      inviteStatus:
        item?.inviteStatus ?? item?.InviteStatus ?? "Initiated",
    };
  }

  /* ============================================================
   *  SUBMIT (CREATE / UPDATE)
   * ============================================================ */
  submit(): void {
    const mobileControl = this.inviteForm.get("mobileNo");

    if (mobileControl?.invalid) {
      mobileControl.markAsTouched();
      this.focusMobileInput();
      return;
    }

    if (this.inviteForm.invalid) {
      this.inviteForm.markAllAsTouched();
      this.focusMobileInput();
      return;
    }

    const societyId = this.getSocietyId();

    if (!societyId) {
      this.toastr.error("Society Id not found. Please refresh and try again.");
      return;
    }

    const buildingId = String(
      this.inviteForm.value.identifier1 || "",
    ).trim();

    const flatId = String(
      this.inviteForm.value.identifier2 || "",
    ).trim();

    const payload = {
      moduleType: "Society",

      moduleId: societyId,

      /* Flat Owner Name */
      primaryName: this.inviteForm.value.primaryName || "",

      /* Flat Member Name */
      secondaryName: this.inviteForm.value.secondaryName || "",

      /* Mandatory */
      mobileNo: String(this.inviteForm.value.mobileNo || "").trim(),

      /* BuildingId */
      identifier1: buildingId,

      /* FlatId */
      identifier2: flatId,

      inviteToken:
        this.editingInvite?.inviteToken ??
        this.editingInvite?.InviteToken ??
        "",

      inviteStatus:
        this.editingInvite?.inviteStatus ??
        this.editingInvite?.InviteStatus ??
        "Initiated",

      claimedByUserId:
        this.editingInvite?.claimedByUserId ??
        this.editingInvite?.ClaimedByUserId ??
        null,

      targetUserId:
        this.editingInvite?.targetUserId ??
        this.editingInvite?.TargetUserId ??
        null,

      completedOn:
        this.editingInvite?.completedOn ??
        this.editingInvite?.CompletedOn ??
        null,

      invitePayloadJson: JSON.stringify({
        FlatOwnerName: this.inviteForm.value.primaryName || "",
        FlatMemberName: this.inviteForm.value.secondaryName || "",
        BuildingId: buildingId,
        FlatId: flatId,
      }),

      actionUser: this.getActionUser(),
    };

    /* ---------- UPDATE ---------- */
    if (this.editingInvite) {
      const inviteId = Number(
        this.editingInvite?.inviteId ??
          this.editingInvite?.InviteId ??
          0,
      );

      if (!inviteId) {
        this.toastr.error("Invite Id not found. Cannot update this record.");
        return;
      }

      const updatePayload = {
        inviteId,
        ...payload,
      };

      console.log("Update Invite Payload", updatePayload);

      this.service.PreEnrollmentInvite_Update(updatePayload).subscribe({
        next: (res: any) => {
          console.log("Update Invite Response", res);
          this.toastr.success("Invite updated successfully");

          this.inviteForm.reset();
          this.editingInvite = null;
          this.flatList = [];
          this.showForm = false;

          this.readInviteList();
        },

        error: (err: any) => {
          console.log("Update Invite Error", err);
          this.toastr.error(
            err?.error?.message ||
              err?.message ||
              "Something went wrong",
          );
        },
      });

      return;
    }

    /* ---------- CREATE ---------- */
    console.log("Create Invite Payload", payload);

    this.service.PreEnrollmentInvite_Create(payload).subscribe({
      next: (res: any) => {
        console.log("Create Invite Response", res);
        this.toastr.success("Invite created successfully");

        this.inviteForm.reset();
        this.flatList = [];
        this.showForm = false;

        this.readInviteList();
      },

      error: (err: any) => {
        console.log("Create Invite Error", err);
        this.toastr.error(
          err?.error?.message ||
            err?.message ||
            "Something went wrong",
        );
      },
    });
  }

  /* ============================================================
   *  BULK EXCEL UPLOAD
   * ============================================================ */
  async handleInviteExcelUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;

    if (!input.files?.length) {
      return;
    }

    const file = input.files[0];

    const societyId = this.getSocietyId();

    if (!societyId) {
      this.toastr.error("Society Id not found. Please refresh and try again.");
      input.value = "";
      return;
    }

    const requiredFields: excelDataAndDataTypeInterface[] = [
      {
        header: "Mobile No",
        name: "mobileNo",
        dataType: "varchar 10",
        required: true,
      },
      {
        header: "Flat Owner Name",
        name: "primaryName",
        dataType: "varchar 150",
        required: false,
      },
      {
        header: "Flat Member Name",
        name: "secondaryName",
        dataType: "varchar 150",
        required: false,
      },
      {
        header: "Building Id",
        name: "identifier1",
        dataType: "varchar 100",
        required: false,
      },
      {
        header: "Flat Id",
        name: "identifier2",
        dataType: "varchar 100",
        required: false,
      },
    ];

    try {
      const fileData =
        await this.bulkUploadService.fileDataValidationAndObjectList(
          requiredFields,
          file,
        );

      if (typeof fileData === "string") {
        this.toastr.error(fileData);
        input.value = "";
        return;
      }

      const tableData: any[] = fileData?.tableData || [];

      if (!tableData.length) {
        this.toastr.warning("Excel file has no valid rows");
        input.value = "";
        return;
      }

      const inviteList = tableData
        .map((row: any) => {
          const mobileNo = this.normalizeInviteMobile(
            this.getInviteExcelValue(row, "Mobile No", "mobileNo"),
          );

          const primaryName = String(
            this.getInviteExcelValue(row, "Flat Owner Name", "primaryName") ||
              "",
          ).trim();

          const secondaryName = String(
            this.getInviteExcelValue(
              row,
              "Flat Member Name",
              "secondaryName",
            ) || "",
          ).trim();

          const identifier1 = String(
            this.getInviteExcelValue(row, "Building Id", "identifier1") || "",
          ).trim();

          const identifier2 = String(
            this.getInviteExcelValue(row, "Flat Id", "identifier2") || "",
          ).trim();

          return {
            moduleType: "Society",
            moduleId: societyId,
            primaryName,
            secondaryName,
            mobileNo,
            identifier1,
            identifier2,
            inviteToken: "",
            inviteStatus: "Initiated",
            claimedByUserId: null,
            targetUserId: null,
            completedOn: null,
            invitePayloadJson: JSON.stringify({
              FlatOwnerName: primaryName,
              FlatMemberName: secondaryName,
              BuildingId: identifier1,
              FlatId: identifier2,
            }),
            actionUser: this.getActionUser(),
          };
        })
        .filter((row: any) => !!row.mobileNo);

      if (!inviteList.length) {
        this.toastr.warning("No valid mobile numbers found");
        input.value = "";
        return;
      }

      const invalidMobileRow = inviteList.findIndex(
        (row: any) => !/^[0-9]{10}$/.test(String(row.mobileNo || "")),
      );

      if (invalidMobileRow >= 0) {
        this.toastr.error(
          `Row ${invalidMobileRow + 2}: Mobile No must be 10 digits`,
        );
        input.value = "";
        return;
      }

      const uniqueInviteList: any[] = [];
      const excelSignatures = new Set<string>();
      let duplicateExcelCount = 0;

      for (const invite of inviteList) {
        const signature = this.getInviteDuplicateSignature(invite);

        if (excelSignatures.has(signature)) {
          duplicateExcelCount++;
          continue;
        }

        excelSignatures.add(signature);
        uniqueInviteList.push(invite);
      }

      if (duplicateExcelCount > 0) {
        this.toastr.warning(
          `${duplicateExcelCount} duplicate row(s) in Excel skipped`,
        );
      }

      if (!uniqueInviteList.length) {
        this.toastr.warning("All Excel rows are duplicate");
        input.value = "";
        return;
      }

      this.validateInviteBulkBeforeCreate(
        uniqueInviteList,
        societyId,
        input,
      );
    } catch (err) {
      console.log(err);
      this.toastr.error("Invalid Excel file");
      input.value = "";
    }
  }

  private getInviteExcelValue(
    row: any,
    header: string,
    name: string,
  ): any {
    return row?.[name] ?? row?.[header] ?? "";
  }

  private normalizeInviteMobile(value: any): string {
    if (value === null || value === undefined) {
      return "";
    }

    return String(value)
      .trim()
      .replace(/\.0+$/, "")
      .replace(/\s+/g, "");
  }

  private normalizeDuplicateText(value: any): string {
    return String(value ?? "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
  }

  private getInviteDuplicateSignature(item: any): string {
    return [
      this.normalizeDuplicateText(
        item?.moduleType ?? item?.ModuleType ?? "Society",
      ),
      String(Number(item?.moduleId ?? item?.ModuleId ?? 0)),
      this.normalizeInviteMobile(item?.mobileNo ?? item?.MobileNo ?? ""),
      this.normalizeDuplicateText(
        item?.primaryName ?? item?.PrimaryName ?? "",
      ),
      this.normalizeDuplicateText(
        item?.secondaryName ?? item?.SecondaryName ?? "",
      ),
      this.normalizeDuplicateText(
        item?.identifier1 ?? item?.Identifier1 ?? "",
      ),
      this.normalizeDuplicateText(
        item?.identifier2 ?? item?.Identifier2 ?? "",
      ),
    ].join("|");
  }

  private validateInviteBulkBeforeCreate(
    inviteList: any[],
    societyId: number,
    input: HTMLInputElement,
  ): void {
    const validatePayload = {
      moduleType: "Society",
      moduleId: societyId,
    };

    console.log("Bulk Invite Validate Payload", validatePayload);

    this.service
      .PreEnrollmentInvite_ReadByModule(validatePayload)
      .subscribe({
        next: (res: any) => {
          const existingRows = this.extractInviteRows(res).map(
            (item: any) => this.normalizeInviteRow(item),
          );

          const existingSignatures = new Set<string>(
            existingRows.map((item: any) =>
              this.getInviteDuplicateSignature(item),
            ),
          );

          const validInviteList = inviteList.filter(
            (item: any) =>
              !existingSignatures.has(
                this.getInviteDuplicateSignature(item),
              ),
          );

          const duplicateCount =
            inviteList.length - validInviteList.length;

          if (duplicateCount > 0) {
            this.toastr.warning(
              `${duplicateCount} existing duplicate invite(s) skipped`,
            );
          }

          if (!validInviteList.length) {
            this.toastr.warning(
              "All selected records already exist. Nothing to upload.",
            );
            input.value = "";
            return;
          }

          this.createBulkInvites(validInviteList, input);
        },

        error: (err: any) => {
          console.log("Bulk Invite Validate Error", err);
          this.toastr.error(
            err?.error?.message ||
              err?.message ||
              "Unable to validate bulk invite duplicates",
          );
          input.value = "";
        },
      });
  }

  private createBulkInvites(
    inviteList: any[],
    input: HTMLInputElement,
  ): void {
    const payload = {
      preEnrollmentInviteList: inviteList,
    };

    console.log("Bulk Invite Payload", payload);

    this.service.PreEnrollmentInvite_CreateBulk(payload).subscribe({
      next: (res: any) => {
        console.log("Bulk Invite Response", res);
        this.toastr.success("Bulk invite created successfully");
        this.readInviteList();
        input.value = "";
      },

      error: (err: any) => {
        console.log("Bulk Invite Error", err);
        this.toastr.error(
          err?.error?.message ||
            err?.message ||
            "Bulk upload failed",
        );
        input.value = "";
      },
    });
  }

  /* ============================================================
   *  HELPERS
   * ============================================================ */
  getSocietyId(): number {
    const user: any = this.User || this.userLoginService.User() || {};

    const candidates = [
      this.selectedSocietyId,

      user?.societyId,
      user?.SocietyId,
      user?.societyID,

      localStorage.getItem("societyId"),
      localStorage.getItem("SocietyId"),
      localStorage.getItem("societyID"),
      localStorage.getItem("selectedSocietyId"),
      localStorage.getItem("currentSocietyId"),
    ];

    for (const candidate of candidates) {
      const societyId = Number(candidate || 0);

      if (Number.isFinite(societyId) && societyId > 0) {
        return societyId;
      }
    }

    return 0;
  }

  getActionUser(): string {
    return String(
      this.User?.userId ||
        this.User?.UserId ||
        localStorage.getItem("userId") ||
        localStorage.getItem("UserId") ||
        "1",
    );
  }

  handleDownloadInviteExcel(): void {
    const link = document.createElement("a");

    link.href =
      "assets/sampleExcelFiles/Housing-society/society pre-enrollment-invite-bulk-template.xlsx";

    link.download = "society pre-enrollment-invite-bulk-template.xlsx";

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  }

  private focusMobileInput(): void {
    setTimeout(() => {
      const input = this.mobileNoInput?.nativeElement;

      if (input) {
        input.focus();
      }
    }, 0);
  }

  /* ============================================================
   *  GETTERS — FILTER / PAGINATION / STATUS COLOR
   * ============================================================ */
  get filteredInviteList() {
    return this.inviteList.filter(
      (x) =>
        String(x?.inviteStatus || "")
          .trim()
          .toLowerCase() ===
        String(this.selectedStatus || "")
          .trim()
          .toLowerCase(),
    );
  }

  get paginatedInviteList(): any[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;

    return this.filteredInviteList.slice(startIndex, endIndex);
  }

  get totalPages(): number {
    return Math.max(
      1,
      Math.ceil(this.filteredInviteList.length / this.pageSize),
    );
  }

  get pageNumbers(): number[] {
    return Array.from(
      { length: this.totalPages },
      (_, index) => index + 1,
    );
  }

  get showingFrom(): number {
    if (!this.filteredInviteList.length) {
      return 0;
    }

    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingTo(): number {
    return Math.min(
      this.currentPage * this.pageSize,
      this.filteredInviteList.length,
    );
  }

  changeStatus(status: string): void {
    this.selectedStatus = status;
    this.currentPage = 1;
    this.activeMenuIndex = null;
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages) {
      return;
    }

    this.currentPage = page;
    this.activeMenuIndex = null;
  }

  getStatusTextColor(status: any): string {
    const normalizedStatus = String(status || "")
      .trim()
      .toLowerCase();

    if (normalizedStatus === "initiated") {
      return "#f59e0b";
    }

    if (normalizedStatus === "processing") {
      return "#0d6efd";
    }

    if (normalizedStatus === "completed") {
      return "#198754";
    }

    return "#6c757d";
  }
}